import { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import BackMethods from "../../back_component/BackMethods";

function ModalHeaderPro({ title, onHide }) {
  return (
    <div className="users-admin-modal__header">
      <div className="users-admin-modal__header-main">
        <h5 className="users-admin-modal__title mb-0">{title}</h5>
      </div>
      <button type="button" className="btn-close" onClick={onHide} aria-label="إغلاق" />
    </div>
  );
}

function InlineField({ label, htmlFor, children }) {
  return (
    <div className="users-admin-modal__inline-field">
      <Form.Label htmlFor={htmlFor} className="users-admin-modal__inline-label">
        {label}
      </Form.Label>
      <div className="users-admin-modal__inline-control">{children}</div>
    </div>
  );
}

function CreateComplaintCategoryModal({ show, onHide, onSaved, defaultDisplayOrder = 0 }) {
  const { backEnd_request } = BackMethods();
  const [form, setForm] = useState({
    name: "",
    displayOrder: 0,
    isActive: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show) return;
    setForm({
      name: "",
      displayOrder: Number(defaultDisplayOrder) || 0,
      isActive: true,
    });
    setFormError("");
    setIsSubmitting(false);
  }, [show, defaultDisplayOrder]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "displayOrder" ? (value === "" ? 0 : parseInt(value, 10) || 0) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name.trim()) {
      setFormError("يرجى إدخال اسم التصنيف.");
      return;
    }
    setIsSubmitting(true);
    try {
      await backEnd_request.post("/ComplaintCategories", {
        name: form.name.trim(),
        displayOrder: form.displayOrder,
        isActive: form.isActive,
      });
      onSaved?.({ message: "تم إضافة التصنيف بنجاح ✅", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "حدث خطأ أثناء الإضافة");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="إضافة تصنيف شكوى" onHide={onHide} />
      <Modal.Body>
        <p className="users-admin-modal__hint">
          <i className="bi bi-plus-circle" />
          <span>حدد اسم التصنيف وترتيب العرض وحالته.</span>
        </p>
        <Form onSubmit={handleSubmit}>
          <Row className="g-2 gx-lg-3">
            <Col md={12}>
              <InlineField label="اسم التصنيف" htmlFor="cc-create-name">
                <Form.Control
                  id="cc-create-name"
                  size="sm"
                  className="rounded-2"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </InlineField>
            </Col>
            <Col md={6}>
              <InlineField label="ترتيب العرض" htmlFor="cc-create-order">
                <Form.Control
                  id="cc-create-order"
                  type="number"
                  size="sm"
                  className="rounded-2"
                  value={form.displayOrder}
                  onChange={(e) => handleChange("displayOrder", e.target.value)}
                />
              </InlineField>
            </Col>
            <Col md={6}>
              <InlineField label="الحالة" htmlFor="cc-create-active">
                <Form.Check
                  type="switch"
                  id="cc-create-active"
                  label={form.isActive ? "نشط" : "متوقف"}
                  checked={form.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                />
              </InlineField>
            </Col>
          </Row>
          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> حفظ
                </>
              ) : (
                "إضافة التصنيف"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

function EditComplaintCategoryModal({ show, categoryId, initialCategory, onHide, onSaved }) {
  const { backEnd_request } = BackMethods();
  const [form, setForm] = useState({
    name: "",
    displayOrder: 0,
    isActive: true,
  });
  const [initialName, setInitialName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show || !categoryId) return;
    setFormError("");
    if (initialCategory) {
      const n = initialCategory.name ?? "";
      setForm({
        name: n,
        displayOrder: initialCategory.displayOrder ?? 0,
        isActive: initialCategory.isActive ?? true,
      });
      setInitialName(n);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const res = await backEnd_request.get(`/ComplaintCategories/${categoryId}`);
        if (cancelled) return;
        const data = res.data || res || {};
        const n = data.name ?? "";
        setForm({
          name: n,
          displayOrder: data.displayOrder ?? 0,
          isActive: data.isActive ?? true,
        });
        setInitialName(n);
      } catch (err) {
        if (!cancelled) {
          setFormError(err.response?.data?.message || "فشل جلب بيانات التصنيف");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, categoryId, initialCategory, backEnd_request]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: field === "displayOrder" ? (value === "" ? 0 : parseInt(value, 10) || 0) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.name.trim()) {
      setFormError("يرجى إدخال اسم التصنيف.");
      return;
    }
    setIsSubmitting(true);
    try {
      await backEnd_request.post(`/ComplaintCategories/${categoryId}`, {
        name: form.name.trim(),
        displayOrder: form.displayOrder,
        isActive: form.isActive,
      });
      onSaved?.({ message: "تم تعديل التصنيف بنجاح ✅", variant: "success" });
      onHide();
    } catch (err) {
      setFormError(err.response?.data?.message || "تعذر حفظ التعديلات");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="users-admin-modal-dialog"
      className="users-admin-modal users-admin-modal--compact"
      dir="rtl"
      backdrop="static"
    >
      <ModalHeaderPro title="تعديل تصنيف شكوى" onHide={onHide} />
      <Modal.Body>
        {initialName ? (
          <div className="users-admin-modal__user-chip mb-2 py-1">
            <i className="bi bi-tags text-secondary small" />
            <span className="text-muted" style={{ fontSize: "0.75rem" }}>
              التصنيف الحالي
            </span>
            <strong className="small">{initialName}</strong>
          </div>
        ) : null}
        <Form onSubmit={handleSubmit}>
          {isLoading ? (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          ) : null}
          <Row className="g-2 gx-lg-3">
            <Col md={12}>
              <InlineField label="اسم التصنيف" htmlFor="cc-edit-name">
                <Form.Control
                  id="cc-edit-name"
                  size="sm"
                  className="rounded-2"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
            <Col md={6}>
              <InlineField label="ترتيب العرض" htmlFor="cc-edit-order">
                <Form.Control
                  id="cc-edit-order"
                  type="number"
                  size="sm"
                  className="rounded-2"
                  value={form.displayOrder}
                  onChange={(e) => handleChange("displayOrder", e.target.value)}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
            <Col md={6}>
              <InlineField label="الحالة" htmlFor="cc-edit-active">
                <Form.Check
                  type="switch"
                  id="cc-edit-active"
                  label={form.isActive ? "نشط" : "متوقف"}
                  checked={form.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                  disabled={isLoading}
                />
              </InlineField>
            </Col>
          </Row>
          {formError ? (
            <div className="alert alert-danger py-1 px-2 small mb-0 mt-2" role="alert">
              {formError}
            </div>
          ) : null}
          <div className="users-admin-modal__footer d-flex gap-2 justify-content-end mt-3">
            <Button variant="outline-secondary" size="sm" type="button" onClick={onHide} disabled={isSubmitting}>
              إلغاء
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="ms-1" /> حفظ
                </>
              ) : (
                "حفظ التغييرات"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default function ComplaintCategoriesModals({
  modal,
  categoryId,
  initialCategory,
  defaultDisplayOrder,
  onClose,
  onSaved,
  canCreate = true,
  canEdit = true,
}) {
  return (
    <>
      <CreateComplaintCategoryModal
        show={canCreate && modal === "create"}
        onHide={onClose}
        onSaved={onSaved}
        defaultDisplayOrder={defaultDisplayOrder}
      />
      <EditComplaintCategoryModal
        show={canEdit && modal === "edit"}
        categoryId={categoryId}
        initialCategory={initialCategory}
        onHide={onClose}
        onSaved={onSaved}
      />
    </>
  );
}
